// src/services/notifications/sms.js
// Client-side SMS service.
//
// Three responsibilities:
//   1. Phone verification — call Firebase callables to send/verify code
//   2. SOS SMS fallback  — if data push fails, compose a pre-filled SMS
//   3. Status helpers    — readable display of SMS enrollment state
//
// The heavy lifting lives in functions/sms.js (Cloud Functions).
// This file is the thin client that calls those functions and
// provides UI-ready helpers.

import { FEATURES }           from '@config';
import { validateSMSContent } from '@services/validation';

// ── Twilio inbound number (where users text to) ───────────────────
// This is the number users text to reach the receiveSMS webhook.
// Format: E.164. Configure in Twilio console + Firebase Functions config.
export const SMS_INBOUND_NUMBER = import.meta.env.VITE_SMS_INBOUND_NUMBER ?? '+15045550100';

// Display-formatted for UI ("(504) 555-0100")
export function formatSMSNumber(e164 = SMS_INBOUND_NUMBER) {
  const digits = e164.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    const [, area, exch, sub] = digits.match(/1(\d{3})(\d{3})(\d{4})/);
    return `(${area}) ${exch}-${sub}`;
  }
  return e164;
}

// ── Firebase callable wrapper ─────────────────────────────────────
async function getCallable(name) {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { getApp } = await import('firebase/app');
  const functions   = getFunctions(getApp());
  return httpsCallable(functions, name);
}

// ── Phone verification ────────────────────────────────────────────
// Returns { sent: true, expiresAt: ISO } or throws HttpsError

export async function sendVerificationCode(phone) {
  if (!FEATURES.firebase) throw new Error('Firebase not configured');
  if (!FEATURES.sms)      throw new Error('SMS not enabled');

  // Validate E.164 format client-side before calling server
  if (!phone || !/^\+\d{7,15}$/.test(phone)) {
    throw new Error('Phone number must be in E.164 format (e.g. +15045550100)');
  }
  const fn     = await getCallable('sendVerificationCode');
  const result = await fn({ phone });
  return result.data;  // { sent, expiresAt }
}

// Returns { verified: true, profileId } or throws HttpsError
export async function verifyPhone(phone, code) {
  if (!FEATURES.firebase) throw new Error('Firebase not configured');

  const fn     = await getCallable('verifyPhone');
  const result = await fn({ phone, code });
  return result.data;  // { verified, profileId }
}

// ── SMS enrollment status ─────────────────────────────────────────
export const SMS_STATUS = {
  NOT_CONFIGURED:  'not_configured',   // VITE_SMS_INBOUND_NUMBER not set
  UNVERIFIED:      'unverified',       // phone saved but not verified
  VERIFIED:        'verified',         // phone verified, SMS active
  DISABLED:        'disabled',         // user has turned SMS off
};

export function getSMSStatus(alertPrefs) {
  if (!FEATURES.sms || !SMS_INBOUND_NUMBER) return SMS_STATUS.NOT_CONFIGURED;
  if (!alertPrefs?.phone)                  return SMS_STATUS.UNVERIFIED;
  if (!alertPrefs?.phoneVerified)          return SMS_STATUS.UNVERIFIED;
  if (!alertPrefs?.channels?.sms)          return SMS_STATUS.DISABLED;
  return SMS_STATUS.VERIFIED;
}

// ── SOS SMS fallback ──────────────────────────────────────────────
// When data connectivity fails entirely and the sync engine's SOS write
// has exhausted retries, offer the user a pre-composed SMS they can send
// from their native Messages app.
//
// This works because SMS uses the cellular voice channel (SS7/2G fallback)
// which survives even when data towers are congested or offline.
//
// Returns: { smsUrl, body, number } so the caller can open the native SMS app.

export function buildSOSFallbackSMS({ body, address, zone, sosSubtype }) {
  // Compose a message the receiveSMS handler can parse
  // Format: "SOS [address] | [message]"
  const parts = [];

  if (address) {
    parts.push(address.trim());
    parts.push('|');
  }

  const typePrefix = {
    trapped:  'TRAPPED',
    medical:  'MEDICAL EMERGENCY',
    food:     'NEED FOOD/WATER',
    check:    'WELFARE CHECK',
    power:    'GENERATOR NEEDED',
  }[sosSubtype] ?? 'EMERGENCY';

  parts.push(typePrefix + (body ? ': ' + body.substring(0, 80) : ''));

  const message = `SOS ${parts.join(' ')}`;

  // Build SMS URL: opens native Messages app on mobile
  const encoded = encodeURIComponent(message.substring(0, 160));
  const smsUrl  = `sms:${SMS_INBOUND_NUMBER}?body=${encoded}`;

  return {
    smsUrl,
    body:    message,
    number:  SMS_INBOUND_NUMBER,
    display: formatSMSNumber(SMS_INBOUND_NUMBER),
  };
}

// ── Phone number validation ───────────────────────────────────────
// Validates and normalises a user-entered phone number to E.164 format.
// Accepts: (504) 555-1234, 504-555-1234, +15045551234, 5045551234
export function normalisePhone(raw) {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
  if (digits.length > 7 && digits[0] !== '1') return `+1${digits.slice(-10)}`;
  return null;
}

export function isValidPhone(raw) {
  return normalisePhone(raw) !== null;
}
